import Phaser from 'phaser';
import {
  APP_WINDOW,
  COLORS,
  DEPTHS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  MENU_BAR,
  TASKBAR,
  toHexColor,
} from '../config/gameConfig';
import type { BadgePayload, EventBus, WindowId } from '../systems/EventBus';

const TITLE_ICON_BY_WINDOW: Record<WindowId, string> = {
  inbox: 'inbox',
  slack: 'message-circle',
  spreadsheet: 'table',
  'field-guide': 'book-open',
  payments: 'circle-dollar-sign',
};

export interface AppWindowOptions {
  x: number;
  y: number;
  width: number;
  height: number;
  title: string;
  windowId: WindowId;
  titleBarHeight?: number;
}

export abstract class App extends Phaser.GameObjects.Container {
  protected readonly bodyContainer: Phaser.GameObjects.Container;
  protected readonly options: Required<AppWindowOptions>;
  protected readonly bus: EventBus;

  private windowWidth: number;
  private windowHeight: number;

  // Maximize state. Snapshot of pre-maximize bounds so restore returns the
  // window to where the player had it, not the static seed position.
  private isMaximized = false;
  private preMaximizeBounds: { x: number; y: number; width: number; height: number } | null = null;

  private shadowRect!: Phaser.GameObjects.Rectangle;
  private borderRect!: Phaser.GameObjects.Rectangle;
  private chromeRect!: Phaser.GameObjects.Rectangle;
  private titleBarRect!: Phaser.GameObjects.Rectangle;
  private titleHighlightRect!: Phaser.GameObjects.Rectangle;
  private dragZoneRect!: Phaser.GameObjects.Rectangle;
  private dragZoneHitArea!: Phaser.Geom.Rectangle;
  private resizeHandleRect!: Phaser.GameObjects.Rectangle;
  private readonly controlDots: Phaser.GameObjects.Arc[] = [];
  private readonly controlRings: Phaser.GameObjects.Arc[] = [];

  private dragStartPointerX = 0;
  private dragStartPointerY = 0;
  private dragStartContainerX = 0;
  private dragStartContainerY = 0;

  private resizeStartPointerX = 0;
  private resizeStartPointerY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;

  private _isMinimized = false;

  private readonly onMinimizedEvent = (p: { windowId: WindowId }): void => {
    if (p.windowId !== this.options.windowId) return;
    this._isMinimized = true;
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scale: 0.9,
      duration: TASKBAR.windowFadeMs,
      onComplete: () => this.setVisible(false),
    });
  };

  private readonly onRestoredEvent = (p: { windowId: WindowId }): void => {
    if (p.windowId !== this.options.windowId) return;
    this._isMinimized = false;
    this.setVisible(true);
    this.bus.emit('window:focus-requested', { windowId: p.windowId });
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets: this,
      alpha: 1,
      scale: 1,
      duration: TASKBAR.windowFadeMs,
      onComplete: () => this.onRestored(),
    });
  };

  private readonly onFocusChanged = (p: {
    focused: WindowId | null;
    stack: readonly WindowId[];
  }): void => {
    let target: number;
    if (p.focused === this.options.windowId) {
      target = DEPTHS.appFocused;
    } else {
      const idx = p.stack.indexOf(this.options.windowId);
      target = idx === -1 ? DEPTHS.appBody : DEPTHS.appBody + idx;
    }
    this.setDepth(target);
    // Phaser's input hit-test sorts every interactive game object by its
    // own depth — container depth is NOT inherited. Without this the
    // top-most window's children and bottom-most window's children both
    // sit at depth 0, so creation order decides who receives clicks.
    this.propagateDepth(this, target);
  };

  private propagateDepth(target: Phaser.GameObjects.GameObject, depth: number): void {
    const withDepth = target as Phaser.GameObjects.GameObject & { setDepth?: (d: number) => unknown };
    withDepth.setDepth?.(depth);
    if (target instanceof Phaser.GameObjects.Container) {
      for (const child of target.list) {
        this.propagateDepth(child, depth);
      }
    }
  }

  /** Re-propagate depth to descendants. Subclasses must call this whenever
   *  they recreate body children (e.g., on tab switch / list refresh). */
  protected refreshChildDepths(): void {
    this.propagateDepth(this, this.depth);
  }


  constructor(scene: Phaser.Scene, bus: EventBus, options: AppWindowOptions) {
    super(scene, options.x, options.y);
    scene.add.existing(this);
    this.setDepth(DEPTHS.appBody);
    this.bus = bus;

    this.options = {
      ...options,
      titleBarHeight: options.titleBarHeight ?? 28,
    };

    this.windowWidth = this.options.width;
    this.windowHeight = this.options.height;

    this.buildChrome(scene);

    const bodyInset = this.options.titleBarHeight + APP_WINDOW.borderWidth * 2;
    this.bodyContainer = scene.add.container(APP_WINDOW.borderWidth, bodyInset);
    this.add(this.bodyContainer);

    this.buildResizeHandle(scene);

    this.bus.on('window:minimized', this.onMinimizedEvent);
    this.bus.on('window:restored', this.onRestoredEvent);
    this.bus.on('window:focus-changed', this.onFocusChanged);

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardownBase());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardownBase());

    this.bus.emit('window:focus-requested', { windowId: this.options.windowId });
    // NOTE: subclasses must call this.renderBody() at the end of their constructor.
  }

  private buildChrome(scene: Phaser.Scene): void {
    const { title, titleBarHeight, windowId } = this.options;
    const w = this.windowWidth;
    const h = this.windowHeight;

    // Soft drop shadow — two stacked rectangles for a layered falloff.
    this.shadowRect = scene.add.rectangle(
      -APP_WINDOW.shadowSpread,
      APP_WINDOW.shadowOffsetY - APP_WINDOW.shadowSpread,
      w + APP_WINDOW.shadowSpread * 2,
      h + APP_WINDOW.shadowSpread * 2,
      COLORS.windowShadow,
      APP_WINDOW.shadowAlpha,
    ).setOrigin(0, 0);
    this.add(this.shadowRect);

    this.borderRect = scene.add.rectangle(0, 0, w, h, COLORS.windowBorder).setOrigin(0, 0);
    this.add(this.borderRect);

    this.chromeRect = scene.add.rectangle(
      APP_WINDOW.borderWidth,
      APP_WINDOW.borderWidth,
      w - APP_WINDOW.borderWidth * 2,
      h - APP_WINDOW.borderWidth * 2,
      COLORS.windowChrome,
    ).setOrigin(0, 0);
    // Make the chrome interactive so it intercepts clicks that would
    // otherwise bleed through to a window underneath (e.g., clicking on
    // the spreadsheet's empty body firing an email row in the inbox below).
    // Children added later in the App's display list sit on top of this
    // rect, so legitimate child clicks (rows, buttons) still work.
    this.chromeRect.setInteractive();
    this.add(this.chromeRect);

    this.titleBarRect = scene.add.rectangle(
      APP_WINDOW.borderWidth,
      APP_WINDOW.borderWidth,
      w - APP_WINDOW.borderWidth * 2,
      titleBarHeight,
      COLORS.windowTitleBar,
    ).setOrigin(0, 0);
    this.add(this.titleBarRect);

    // Subtle 1px highlight at the top of the title bar.
    this.titleHighlightRect = scene.add.rectangle(
      APP_WINDOW.borderWidth,
      APP_WINDOW.borderWidth,
      w - APP_WINDOW.borderWidth * 2,
      1,
      COLORS.windowTitleHighlight,
      APP_WINDOW.titleHighlightAlpha,
    ).setOrigin(0, 0);
    this.add(this.titleHighlightRect);

    const titleY = APP_WINDOW.borderWidth + titleBarHeight / 2;
    const titleIconSize = 13;
    const titleIcon = scene.add
      .image(APP_WINDOW.titlePaddingX + titleIconSize / 2, titleY, `icon:${TITLE_ICON_BY_WINDOW[windowId]}`)
      .setDisplaySize(titleIconSize, titleIconSize)
      .setOrigin(0.5, 0.5)
      .setAlpha(0.85);
    this.add(titleIcon);

    const titleText = scene.add.text(
      APP_WINDOW.titlePaddingX + titleIconSize + 8,
      titleY,
      title,
      {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(COLORS.windowTitleText),
        fontStyle: '500',
      },
    ).setOrigin(0, 0.5);
    this.add(titleText);

    // Window controls — minimize (yellow) and maximize (green). Close is
    // intentionally absent: windows in this game are persistent, so a close
    // button would be a button without a purpose.
    const controlY = APP_WINDOW.borderWidth + titleBarHeight / 2;
    const controls = [
      { fill: COLORS.windowControlMinimize, ring: COLORS.controlRingMinimize },
      { fill: COLORS.windowControlMaximize, ring: COLORS.controlRingMaximize },
    ];
    controls.forEach((ctl, i) => {
      const cx = w - APP_WINDOW.controlRightMargin - (controls.length - 1 - i) * APP_WINDOW.controlDotSpacing;
      const outer = scene.add.circle(cx, controlY, APP_WINDOW.controlDotRadius, ctl.ring);
      this.add(outer);
      this.controlRings.push(outer);
      const dot = scene.add.circle(cx, controlY, APP_WINDOW.controlDotInnerRadius, ctl.fill);
      this.add(dot);
      dot.setInteractive({ useHandCursor: true });
      if (ctl.fill === COLORS.windowControlMinimize) {
        dot.on('pointerdown', () => {
          this.bus.emit('window:minimize-requested', { windowId });
        });
      } else if (ctl.fill === COLORS.windowControlMaximize) {
        dot.on('pointerdown', () => this.toggleMaximize());
      }
      this.controlDots.push(dot);
    });

    // Drag hit zone (excludes control dots).
    const controlReservedWidth =
      controls.length * APP_WINDOW.controlDotSpacing + APP_WINDOW.controlRightMargin;
    const dragZoneWidth = w - APP_WINDOW.borderWidth * 2 - controlReservedWidth;
    this.dragZoneRect = scene.add.rectangle(
      APP_WINDOW.borderWidth,
      APP_WINDOW.borderWidth,
      dragZoneWidth,
      titleBarHeight,
      0x000000,
      0,
    ).setOrigin(0, 0);
    this.dragZoneHitArea = new Phaser.Geom.Rectangle(0, 0, dragZoneWidth, titleBarHeight);
    this.dragZoneRect.setInteractive({
      hitArea: this.dragZoneHitArea,
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
      draggable: true,
    });
    this.add(this.dragZoneRect);

    this.dragZoneRect.on('dragstart', (pointer: Phaser.Input.Pointer) => {
      this.dragStartPointerX = pointer.x;
      this.dragStartPointerY = pointer.y;
      this.dragStartContainerX = this.x;
      this.dragStartContainerY = this.y;
    });
    this.dragZoneRect.on('drag', (pointer: Phaser.Input.Pointer) => {
      const nextX = this.dragStartContainerX + (pointer.x - this.dragStartPointerX);
      const nextY = this.dragStartContainerY + (pointer.y - this.dragStartPointerY);
      this.x = Phaser.Math.Clamp(nextX, 0, GAME_WIDTH - this.windowWidth);
      this.y = Phaser.Math.Clamp(nextY, MENU_BAR.height, GAME_HEIGHT - TASKBAR.height - this.windowHeight);
    });
  }

  private buildResizeHandle(scene: Phaser.Scene): void {
    const handleSize = APP_WINDOW.resizeHandleSize;
    this.resizeHandleRect = scene.add.rectangle(
      this.windowWidth - handleSize - APP_WINDOW.borderWidth,
      this.windowHeight - handleSize - APP_WINDOW.borderWidth,
      handleSize,
      handleSize,
      COLORS.windowBorder,
    ).setOrigin(0, 0);
    this.resizeHandleRect.setInteractive({ useHandCursor: true, draggable: true });
    this.add(this.resizeHandleRect);

    this.resizeHandleRect.on('dragstart', (pointer: Phaser.Input.Pointer) => {
      this.resizeStartPointerX = pointer.x;
      this.resizeStartPointerY = pointer.y;
      this.resizeStartWidth = this.windowWidth;
      this.resizeStartHeight = this.windowHeight;
    });
    this.resizeHandleRect.on('drag', (pointer: Phaser.Input.Pointer) => {
      const dx = pointer.x - this.resizeStartPointerX;
      const dy = pointer.y - this.resizeStartPointerY;
      const proposedW = this.resizeStartWidth + dx;
      const proposedH = this.resizeStartHeight + dy;
      const maxW = GAME_WIDTH - this.x;
      const maxH = GAME_HEIGHT - TASKBAR.height - this.y;
      this.windowWidth = Phaser.Math.Clamp(proposedW, APP_WINDOW.minWidth, maxW);
      this.windowHeight = Phaser.Math.Clamp(proposedH, APP_WINDOW.minHeight, maxH);
      this.relayoutChrome();
      this.onResize();
    });
  }

  private relayoutChrome(): void {
    const w = this.windowWidth;
    const h = this.windowHeight;
    const titleBarHeight = this.options.titleBarHeight;

    this.shadowRect.setSize(w + APP_WINDOW.shadowSpread * 2, h + APP_WINDOW.shadowSpread * 2);
    this.borderRect.setSize(w, h);
    this.chromeRect.setSize(w - APP_WINDOW.borderWidth * 2, h - APP_WINDOW.borderWidth * 2);
    this.titleBarRect.setSize(w - APP_WINDOW.borderWidth * 2, titleBarHeight);
    this.titleHighlightRect.setSize(w - APP_WINDOW.borderWidth * 2, 1);

    // Control rings + dots sit at the same x; update both.
    const n = this.controlDots.length;
    this.controlDots.forEach((dot, i) => {
      dot.x = w - APP_WINDOW.controlRightMargin - (n - 1 - i) * APP_WINDOW.controlDotSpacing;
    });
    this.controlRings.forEach((ring, i) => {
      ring.x = w - APP_WINDOW.controlRightMargin - (n - 1 - i) * APP_WINDOW.controlDotSpacing;
    });

    const controlReservedWidth =
      this.controlDots.length * APP_WINDOW.controlDotSpacing + APP_WINDOW.controlRightMargin;
    const dragZoneWidth = w - APP_WINDOW.borderWidth * 2 - controlReservedWidth;
    this.dragZoneRect.setSize(dragZoneWidth, titleBarHeight);
    this.dragZoneHitArea.width = dragZoneWidth;
    this.dragZoneHitArea.height = titleBarHeight;

    const handleSize = APP_WINDOW.resizeHandleSize;
    this.resizeHandleRect.x = w - handleSize - APP_WINDOW.borderWidth;
    this.resizeHandleRect.y = h - handleSize - APP_WINDOW.borderWidth;
  }

  /** Toggle the window between user-sized and full desktop. The desktop
   *  area is everything between the menu bar (top) and the taskbar (bottom). */
  private toggleMaximize(): void {
    if (this.isMaximized) {
      const b = this.preMaximizeBounds;
      this.preMaximizeBounds = null;
      this.isMaximized = false;
      if (b) {
        this.x = b.x;
        this.y = b.y;
        this.windowWidth = b.width;
        this.windowHeight = b.height;
      }
    } else {
      this.preMaximizeBounds = {
        x: this.x,
        y: this.y,
        width: this.windowWidth,
        height: this.windowHeight,
      };
      this.isMaximized = true;
      this.x = 0;
      this.y = MENU_BAR.height;
      this.windowWidth = GAME_WIDTH;
      this.windowHeight = GAME_HEIGHT - MENU_BAR.height - TASKBAR.height;
    }
    this.relayoutChrome();
    this.onResize();
    this.bus.emit('window:focus-requested', { windowId: this.options.windowId });
  }

  protected abstract renderBody(): void;

  /** Each subclass reports its current badge payload for the taskbar tile. */
  abstract getBadge(): BadgePayload;

  /** Hook called after restore animation completes. Override to reset counters. */
  protected onRestored(): void {
    // default no-op
  }

  /** Hook called after the window has been resized. Override to re-render body content at new size. */
  protected onResize(): void {
    // default no-op
  }

  get isMinimized(): boolean {
    return this._isMinimized;
  }

  get windowId(): WindowId {
    return this.options.windowId;
  }

  get bodyWidth(): number {
    return this.windowWidth - APP_WINDOW.borderWidth * 2;
  }

  get bodyHeight(): number {
    return this.windowHeight - this.options.titleBarHeight - APP_WINDOW.borderWidth * 2;
  }

  get titleBarHeight(): number {
    return this.options.titleBarHeight;
  }

  containsPoint(x: number, y: number): boolean {
    if (!this.visible || this._isMinimized) return false;
    return x >= this.x && x < this.x + this.windowWidth && y >= this.y && y < this.y + this.windowHeight;
  }

  private teardownBase(): void {
    this.bus.off('window:minimized', this.onMinimizedEvent);
    this.bus.off('window:restored', this.onRestoredEvent);
    this.bus.off('window:focus-changed', this.onFocusChanged);
  }
}
